import type { ProductService } from '../../../../types/order.types';
import { isActiveService } from '../../../../util/service-catalog';

/**
 * DV-3204: the floor plan tier choice offered beside a package flagged
 * `includesFreeFloorPlan`.
 *
 * The cart never orders the free plan. order-service adds the $0 Basic Floor
 * Plan line itself when the order is created (DV-3206), so picking Basic sends
 * nothing. Until the paid tiers launch, Basic is the only orderable tier: it
 * comes preselected and cannot be unselected, and the paid tiers show as
 * Coming Soon, with no price.
 *
 * Which packages trigger the choice comes from the catalog flag, never from
 * service names. Which services ARE the tiers is hardcoded below, because the
 * catalog has no field marking a service as a floor plan tier — one of the
 * DB gaps listed in docs/engineering-backlog.md (DV-3204).
 */

/** The slug of the free line order-service adds (its rollout's `freeServiceSlug`). */
export const BASIC_FLOOR_PLAN_SLUG = 'basic_floor_plan';

export type FloorPlanTierId = 'basic' | 'premium' | '3d';

interface TierDefinition {
  id: FloorPlanTierId;
  slug: string;
  /**
   * Hardcoded until the paid tiers launch: only Basic can be ordered. The
   * paid tiers are shown as Coming Soon whether or not they are active, which
   * is safe only while they cannot be picked. Making one orderable must also
   * require `isActiveService` (DV-3586), or a retired tier becomes orderable.
   */
  orderable: boolean;
}

const TIER_DEFINITIONS: readonly TierDefinition[] = [
  { id: 'basic', slug: BASIC_FLOOR_PLAN_SLUG, orderable: true },
  { id: 'premium', slug: 'premium_floor_plan', orderable: false },
  { id: '3d', slug: '3d_floor_plan', orderable: false }
];

export interface FloorPlanTier {
  id: FloorPlanTierId;
  slug: string;
  name: string;
  /** Basic is always 0: order-service prices its line at $0 whatever the catalog says. */
  price: number;
  orderable: boolean;
  /** The catalog's card image (DV-3808); null when the service has none. */
  onlineImage: string | null;
}

export type FloorPlanUnavailableReason =
  | 'catalog-error'
  | 'basic-missing'
  | 'basic-inactive';

export type FloorPlanOffer =
  | { status: 'loading' }
  | { status: 'unavailable'; reason: FloorPlanUnavailableReason }
  | {
      status: 'available';
      tiers: FloorPlanTier[];
      flaggedSlugs: ReadonlySet<string>;
      /** Paid tiers left out because the catalog has no name or price for them. */
      missingTierSlugs: string[];
    };

/**
 * Slugs compared the way order-service compares them (`slugKey` in
 * FreeFloorPlanServiceImpl): trimmed and lower-cased, so the cart and the
 * order never disagree over casing.
 */
export function slugKey(slug: string | null | undefined): string {
  return (slug ?? '').trim().toLowerCase();
}

/** The free tier: always "Free", never priced, whatever else changes. */
export function isFreeTier(tier: FloorPlanTier): boolean {
  return tier.id === 'basic';
}

/** A one-off product's price: the quantity-1 entry, else the default pricing. */
function standalonePrice(service: ProductService): number | null {
  return (
    service.map?.['1']?.totalPrice ?? service.defaultPricing?.totalPrice ?? null
  );
}

/** A service's card image, or null when it has none: blank counts as none (DV-3808). */
function catalogImage(service: ProductService): string | null {
  return service.onlineImage?.trim() || null;
}

/**
 * The free Basic Floor Plan's card image (DV-3808), for the one place that
 * shows the line without the offer: order confirmation, whose image lookup
 * is built from the cart's services, which never include it.
 */
export function freeFloorPlanImage(catalog: ProductService[]): string | null {
  const basic = catalog.find(
    (s) => slugKey(s.shoppingCartSlug) === BASIC_FLOOR_PLAN_SLUG
  );
  return basic ? catalogImage(basic) : null;
}

export function buildFloorPlanOffer(catalog: ProductService[]): FloorPlanOffer {
  const bySlug = new Map(catalog.map((s) => [slugKey(s.shoppingCartSlug), s]));

  // Without a named Basic there is nothing to offer: no free tier card and no
  // cart line, so badging packages would promise what the cart cannot show.
  const basic = bySlug.get(BASIC_FLOOR_PLAN_SLUG);
  if (!basic?.name) {
    return { status: 'unavailable', reason: 'basic-missing' };
  }
  if (!isActiveService(basic)) {
    return { status: 'unavailable', reason: 'basic-inactive' };
  }

  const tiers: FloorPlanTier[] = [];
  const missingTierSlugs: string[] = [];
  for (const definition of TIER_DEFINITIONS) {
    const product = bySlug.get(definition.slug);
    const price =
      definition.id === 'basic' ? 0 : product ? standalonePrice(product) : null;
    if (!product?.name || price === null) {
      missingTierSlugs.push(definition.slug);
      continue;
    }
    tiers.push({
      ...definition,
      name: product.name,
      price,
      onlineImage: catalogImage(product)
    });
  }

  const flaggedSlugs = new Set(
    catalog
      .filter((s) => s.includesFreeFloorPlan === true)
      .map((s) => slugKey(s.shoppingCartSlug))
  );

  return { status: 'available', tiers, flaggedSlugs, missingTierSlugs };
}

/**
 * Would a property carrying these services get the free line, in an account
 * the rollout covers? Any flagged service qualifies it, unless it already
 * carries one of the rollout's floor plan products (`floorPlanSlugs`, from the
 * account's eligibility read, already in `slugKey` form).
 *
 * ASSUMPTION: only packages are flagged. The offer is built from the package
 * list, so a flagged add-on is unknown here: order-service would add the line
 * and the cart would not show it (it under-promises, never over-promises).
 * Banked in docs/engineering-backlog.md (DV-3204).
 */
export function freeFloorPlanQualifies(
  offer: FloorPlanOffer,
  slugs: string[],
  floorPlanSlugs: ReadonlySet<string>
): boolean {
  if (offer.status !== 'available') return false;
  const keys = slugs.map(slugKey);
  if (keys.some((key) => floorPlanSlugs.has(key))) return false;
  return keys.some((key) => offer.flaggedSlugs.has(key));
}

/** Is this order line the free Basic Floor Plan line order-service added? */
export function isFreeFloorPlanLine(line: {
  shoppingCartSlug: string;
}): boolean {
  return slugKey(line.shoppingCartSlug) === BASIC_FLOOR_PLAN_SLUG;
}
